import type { ElementType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type SectionCardTone = 'default' | 'subtle' | 'inset' | 'accent' | 'hero';
export type SectionCardRadius = 'sm' | 'lg' | 'xl' | '2xl';
export type SectionCardElevation = 'flat' | 'ring' | 'lift';

type SectionCardProps = {
  as?: ElementType;
  tone?: SectionCardTone;
  radius?: SectionCardRadius;
  elevation?: SectionCardElevation;
  accents?: boolean;
  padding?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  className?: string;
  id?: string;
  ariaLabelledBy?: string;
  ariaLabel?: string;
  role?: string;
};

const TONE_CLASS: Record<SectionCardTone, string> = {
  default: 'border border-fin-gray-800 bg-fin-gray-900/30',
  subtle: 'border border-fin-gray-800 bg-fin-gray-900/20',
  inset: 'border border-fin-gray-800 bg-fin-black/40',
  accent: 'border border-fin-accent/30 bg-fin-accent/[0.06]',
  hero: 'border border-fin-gray-800/80 bg-fin-gray-900/50',
};

const RADIUS_CLASS: Record<SectionCardRadius, string> = {
  sm: 'rounded-sm',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
};

const ELEVATION_CLASS: Record<SectionCardElevation, string> = {
  flat: '',
  ring: 'ring-1 ring-fin-gray-800/30',
  lift: 'shadow-fin-black/30 ring-1 ring-fin-gray-800/40 shadow-lg',
};

const PADDING_CLASS = {
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
} as const;

export function SectionCard({
  as: Tag = 'section',
  tone = 'default',
  radius = 'xl',
  elevation = 'flat',
  accents = false,
  padding = 'md',
  children,
  className,
  id,
  ariaLabelledBy,
  ariaLabel,
  role,
}: SectionCardProps) {
  return (
    <Tag
      id={id}
      className={cn(
        RADIUS_CLASS[radius],
        TONE_CLASS[tone],
        PADDING_CLASS[padding],
        ELEVATION_CLASS[elevation],
        accents && 'relative overflow-hidden',
        className
      )}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      role={role}
    >
      {accents ? (
        <>
          <div
            className='bg-fin-accent/10 animate-fin-drift pointer-events-none absolute -top-24 -right-24 size-72 rounded-full blur-3xl will-change-transform motion-reduce:animate-none'
            aria-hidden
          />
          <div
            className='bg-fin-purple/10 animate-fin-drift-slow pointer-events-none absolute -bottom-20 -left-16 size-56 rounded-full blur-3xl will-change-transform motion-reduce:animate-none'
            aria-hidden
          />
          <div className='relative'>{children}</div>
        </>
      ) : (
        children
      )}
    </Tag>
  );
}
